import { Box, Button, Container, Paper, Typography } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { useCopy } from '@/hooks/useCopy';
import { redirectToCentralizedLogin } from '@/auth/silentRefresh';

/**
 * errorCode → { key, fallback } for the body copy. Module scope (resolved once,
 * not per render). Keep the six operator-deny codes in sync with the backend
 * contract (SASATH-0133 §2.5). The title stays constant; only the body varies.
 * Exported for the unit test.
 */
export const ERROR_BODY: Record<string, { key: string; fallback: string }> = {
  OPERATOR_NOT_RECOGNIZED: {
    key: 'auth.error.operatorNotRecognized',
    fallback: "This account isn't recognized as a platform operator.",
  },
  OPERATOR_DIRECTORY_REJECTED: {
    key: 'auth.error.operatorDirectoryRejected',
    fallback: "Your organization's directory declined the sign-in.",
  },
  INVALID_OAUTH_STATE: {
    key: 'auth.error.invalidOauthState',
    fallback: 'The sign-in request expired or was invalid. Please start again.',
  },
  PROVIDER_NOT_ALLOWED: {
    key: 'auth.error.providerNotAllowed',
    fallback: "This sign-in provider isn't permitted for the console.",
  },
  OAUTH_TENANT_MISMATCH: {
    key: 'auth.error.oauthTenantMismatch',
    fallback: 'This sign-in was issued for a different application.',
  },
  OAUTH_FAILED: {
    key: 'auth.error.oauthFailed',
    fallback: 'Sign-in could not be completed. Please try again.',
  },
};

export function AuthError() {
  const { t } = useCopy();
  const errorCode = new URLSearchParams(useLocation().search).get('errorCode');
  const entry = errorCode ? ERROR_BODY[errorCode] : undefined;
  const body = entry
    ? t(entry.key, entry.fallback)
    : t('auth.errorBody', 'Your session could not be restored. Try signing in again.');

  return (
    <Container maxWidth="sm" sx={{ py: 8 }}>
      <Paper sx={{ p: 4 }} elevation={1}>
        <Typography variant="h1" component="h1" gutterBottom>
          {t('auth.errorTitle', 'Sign-in could not be completed')}
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          {body}
        </Typography>
        <Box>
          <Button variant="contained" onClick={() => redirectToCentralizedLogin('/')}>
            {t('auth.tryAgain', 'Try again')}
          </Button>
        </Box>
        {errorCode ? (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 3 }}>
            {t('auth.errorReference', 'Reference: {{code}}', { code: errorCode })}
          </Typography>
        ) : null}
      </Paper>
    </Container>
  );
}
